import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, DialogActions, DialogContent, DialogTitle, Stack, Typography, styled } from 'netline-ui';
import { getMainConfig, resolveEnvConfig } from 'utils';

import { LicenseFooter, useAboutModules } from '../../../Navigation';
import UrlIcon from '../../../UrlIcon/UrlIcon';

import ContentType from './consts';
import styles, { generalContentClasses } from './GeneralContent.styles';

const GeneralContentRoot = styled('div', { name: generalContentClasses.$name })(styles);

type GeneralContentProps = {
  onContentChange: (newContentType: ContentType | null) => void;
};

const GeneralContent = ({ onContentChange }: GeneralContentProps) => {
  const { t } = useTranslation();

  const { activeModule, airlineLogoUrl, dialogTitle, licenseUrl, tailLogoUrl } = useAboutModules(
    generalContentClasses.domainName,
  );
  const { contactInfo } = activeModule ?? {};
  const { phone, email } = contactInfo ?? {};
  const contactIsAvailable = phone || email;
  const { title: env } = resolveEnvConfig(getMainConfig('env'));

  const handleVersionsClick = useCallback(() => onContentChange(ContentType.VERSIONS), [onContentChange]);
  const handleSupportClick = useCallback(() => onContentChange(ContentType.SUPPORT), [onContentChange]);
  const handleCloseClick = useCallback(() => onContentChange(null), [onContentChange]);

  return (
    <GeneralContentRoot className={generalContentClasses.root}>
      {tailLogoUrl && <img className={generalContentClasses.tailLogo} src={tailLogoUrl} alt="Tail logo" title="Tail" />}
      <DialogTitle disableTypography variant="h3" onClose={handleCloseClick}>
        <Typography variant="h3" component="span" className={generalContentClasses.titleText}>
          {t('about.title')}
        </Typography>
      </DialogTitle>
      <DialogContent className={generalContentClasses.dialogContent}>
        <div className={generalContentClasses.dialogHeader}>
          {airlineLogoUrl && <UrlIcon className={generalContentClasses.airlineLogo} nameOrUrl={airlineLogoUrl} />}
          <div className={generalContentClasses.title}>{dialogTitle}</div>
          {env && (
            <Typography variant="body2" className={generalContentClasses.environment}>
              {env}
            </Typography>
          )}
        </div>
        <Stack className={generalContentClasses.actions} spacing={2} direction="row">
          <Button onClick={handleVersionsClick} secondary size="small">
            {t('about.see_version_info')}
          </Button>
          {contactIsAvailable && (
            <Button secondary onClick={handleSupportClick} size="small">
              {t('about.support')}
            </Button>
          )}
        </Stack>
      </DialogContent>
      <DialogActions className={generalContentClasses.dialogActions}>
        <LicenseFooter licenseUrl={licenseUrl} />
      </DialogActions>
    </GeneralContentRoot>
  );
};

export default GeneralContent;
